import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTable } from "components/UI/Table/hooks/useTable.ts";
import { PropsWithChildren, useState } from "react";
import { useRecoilValue } from "recoil";
import { Identifiable, TableId } from "types/models.ts";

import { DragIndexContext } from "./context";
import { DragPreview } from "./DragPreview";
import { DnDSortingStrategy, DragIndexState } from "./type";
import { TableLocalStorageConfig } from "../../types/table";

import type { UniqueIdentifier } from "@dnd-kit/core";

interface DnDWrapperProps<T extends Identifiable> extends PropsWithChildren {
    tableId: TableId;
    sortingStrategy: DnDSortingStrategy;
    dataSource?: T[];
    onReorder?: (newData: T[]) => void;
}

export const DnDWrapper = <T extends Identifiable>({
    tableId,
    sortingStrategy = "horizontal",
    children,
    dataSource,
    onReorder,
}: DnDWrapperProps<T>) => {
    const tableUtils = useTable(tableId);
    const [dragIndex, setDragIndex] = useState<DragIndexState>({ active: -1, over: -1 });

    const tableAtomInstance = tableAtom<T>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const columns = tableState.originColumns ?? [];

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }));

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (!active.id || !over?.id || active.id === over.id) return;

        if (sortingStrategy === "horizontal") {
            const activeIndex = columns.findIndex((i) => i.dataIndex === active.id);
            const overIndex = columns.findIndex((i) => i.dataIndex === over.id);
            if (activeIndex < 0 || overIndex < 0) return;

            const newOrder = arrayMove(columns, activeIndex, overIndex);
            const config = tableState.config;
            if (config) {
                const updatedConfig = {
                    ...config,
                    columnsConfig: newOrder.map((column, index) => {
                        const clean = config.columnsConfig.find((c) => c.dataIndex === column.dataIndex)!;
                        return { ...clean, order: index + 1, title: String(column.title) };
                    }),
                } as TableLocalStorageConfig<Identifiable>;

                tableUtils.saveConfig(updatedConfig);
            }
        } else if (sortingStrategy === "vertical" && dataSource && onReorder) {
            const src = dataSource.findIndex((i) => i.id === active.id);
            const dest = dataSource.findIndex((i) => i.id === over.id);
            if (src < 0 || dest < 0) return;

            onReorder(arrayMove(dataSource, src, dest));
        }

        setDragIndex({ active: -1, over: -1 });
    };

    const onDragOver = ({ active, over }: DragOverEvent) => {
        if (!active.id || !over?.id) return;

        const getIndex = () =>
            sortingStrategy === "horizontal"
                ? columns.findIndex((i) => i.dataIndex === active.id)
                : (dataSource?.findIndex((i) => i.id === active.id) ?? 0);

        const getIndexOver = () =>
            sortingStrategy === "horizontal"
                ? columns.findIndex((i) => i.dataIndex === over.id)
                : (dataSource?.findIndex((i) => i.id === over.id) ?? 0);

        const ai = getIndex(),
            oi = getIndexOver();
        setDragIndex({ active: active.id, over: over.id, direction: oi > ai ? "right" : "left" });
    };

    const items: UniqueIdentifier[] =
        sortingStrategy === "horizontal"
            ? columns.map((c) => String(c.dataIndex))
            : (dataSource ?? []).map((i) => String(i.id));

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            modifiers={[sortingStrategy === "horizontal" ? restrictToHorizontalAxis : restrictToVerticalAxis]}
        >
            <SortableContext
                items={items}
                strategy={
                    sortingStrategy === "horizontal" ? horizontalListSortingStrategy : verticalListSortingStrategy
                }
            >
                <DragIndexContext.Provider value={dragIndex}>{children}</DragIndexContext.Provider>
            </SortableContext>
            <DragOverlay>
                {sortingStrategy === "horizontal" ? (
                    <span style={{ backgroundColor: "gray", padding: 16 }}>
                        {columns?.[columns?.findIndex((i) => i.key === dragIndex.active)]?.title as React.ReactNode}
                    </span>
                ) : (
                    <DragPreview<T> columns={columns} dataSource={dataSource ?? []} />
                )}
            </DragOverlay>
        </DndContext>
    );
};
